import {
    AddressDto,
    ConnectorDtoConnectorStateEnum,
    DeviceDto,
    DeviceStatusSrm,
} from "@/api/openapi";
import {
    ENABLE_PERMISSION_INSPECTOR,
    EXTERNAL_ORGANISATION_ID_HEADER,
    FeatureFlag,
    SERVICE_MODEL_TAG_PREFIX,
    SITE_TAG_PREFIX,
} from "@/constants";
import { DeviceDtoDeviceType } from "@/api/query.schemas";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import semver from "semver";

export const makeId = (length: number) => {
    let result = "";
    const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(
            Math.floor(Math.random() * charactersLength),
        );
    }

    return result;
};

export const formatAddress = (address: AddressDto) => {
    const result: Array<string> = [];
    if (address.address2)
        //Address 2 goes first (unit/apartment num)
        result.push(address.address2);
    if (address.address1) result.push(address.address1);
    if (address.city) result.push(address.city);
    if (address.stateCode) result.push(address.stateCode);
    if (address.postCode) result.push(address.postCode);
    if (address.countryCode) result.push(address.countryCode);
    return result.join(", ");
};

/**
 * Validates the returned API error code to be a success
 * For the error code reference see: https://dev.azure.com/jetcharge/Engineering/_git/jc-illuminate?path=/JETCharge.Common/ApiResult.cs&version=GBdev&_a=contents
 * These codes are not exported into the frontend and require hardcoding of custom handling where applicable
 * @param errNo Error code
 * @returns true for Success and False for Error
 */
export const isSuccessApiErrorCode = (errNo?: number): boolean =>
    typeof errNo === "number" && (errNo === 0 || errNo >= 9000);

// Archived or passthrough devices do not support SignalR updates
export const isDeviceThatSupportsSignalRUpdates = (
    device: DeviceDto,
): boolean => !device.deviceArchiveId && !device.passthroughConfigId;

export const mergeDeviceStatusSrmIntoDeviceDto = (
    device?: DeviceDto,
    deviceStatusSrm?: DeviceStatusSrm,
): DeviceDto | undefined => {
    if (!device || !deviceStatusSrm) return device;
    const newDevice: DeviceDto = {
        ...device,
        online: deviceStatusSrm.isOnline,
        simpleDeviceState: deviceStatusSrm.simpleDeviceState,
        status: deviceStatusSrm.status,
    };

    if (Array.isArray(newDevice.connectors)) {
        const updatedConnectors = newDevice.connectors.map((connector) => {
            const updatedInfo = deviceStatusSrm.connectors?.find(
                (c) => c.connectorIndex === connector.connectorIndex,
            );
            if (updatedInfo) {
                return {
                    ...connector,
                    connectorState:
                        updatedInfo.simpleConnectorState as ConnectorDtoConnectorStateEnum,
                    connectorStatus: updatedInfo.state,
                };
            }
            return connector;
        });
        newDevice.connectors = updatedConnectors;
    }

    return newDevice;
};

export const formatDecimalToFixedStripTrailing = (
    value: number,
    decimals = 4,
) => value.toFixed(decimals).replace(/(\.\d*?[1-9])0+$|\.0+$/, "$1");

/**
 * Formats the watt hours for the UI display. Shows Wh, kWh, MWh, GWh depending on the energy value.
 */
export const formatEnergyUsage = (
    energy: number,
    allowNegativeEnergy?: boolean,
): string => {
    const sign = Math.sign(energy);

    if (sign === -1 && !allowNegativeEnergy) {
        return "-- kWh";
    }
    return formatSIUnits(energy, "Wh");
};

export const formatSIUnits = (value: number, unitSuffix: string): string => {
    const { value: convertedValue, unitSuffix: convertedSuffix } =
        convertSIUnits(value, unitSuffix);
    return `${convertedValue.toString()} ${convertedSuffix}`;
};

export const convertSIUnits = (
    value: number,
    unitSuffix: string,
): { value: number; unitSuffix: string } => {
    const sign = Math.sign(value);
    const absValue = Math.abs(value);

    if (absValue < 1000) {
        return { value, unitSuffix };
    } else if (absValue >= 1000 && absValue < 10 ** 6) {
        return {
            value: (sign * Math.floor(absValue / 10)) / 100,
            unitSuffix: `k${unitSuffix}`,
        };
    } else if (absValue >= 10 ** 6 && absValue < 10 ** 9) {
        return {
            value: (sign * Math.floor(absValue / 10 ** 4)) / 100,
            unitSuffix: `M${unitSuffix}`,
        };
    } else if (absValue >= 10 ** 9 && absValue < 10 ** 12) {
        return {
            value: (sign * Math.floor(absValue / 10 ** 7)) / 100,
            unitSuffix: `G${unitSuffix}`,
        };
    } else {
        return {
            value: (sign * Math.floor(absValue / 10 ** 10)) / 100,
            unitSuffix: `T${unitSuffix}`,
        };
    }
};

export const convertcO2PerWhToc02PerKWh = (cO2PerWh: number) => {
    return cO2PerWh * 1000;
};

export const stringToHtmlIdString = (s: string) =>
    `id_${s.replace(/\W/g, "_")}`.toLowerCase();

export const findKeyInObject = (key: string, object: object) => {
    let result: unknown;
    Object.keys(object).some(function (currentKey) {
        const value = object[currentKey as keyof object];
        if (currentKey === key) {
            result = value;
            return true;
        }
        if (value && typeof value === "object") {
            result = findKeyInObject(key, value);
            return result !== undefined;
        }
    });

    return result;
};

export interface IEmailParams {
    to?: string;
    /**
     * Multiple CC addresses may be separated by semicolon
     */
    ccs?: string;
    subject?: string;
    body?: string;
}
export function decodeMailtoLink(mailto: string): IEmailParams {
    const uri = new URL(mailto);
    if (uri.protocol != "mailto:") return {};
    const to = uri.href.split(":")[1].split("?")[0];
    const queryParams = uri.searchParams;
    const cc = queryParams.get("cc");
    const subject = queryParams.get("subject");
    const body = queryParams.get("body");
    return {
        to: to,
        ccs: cc ?? undefined,
        subject: subject ?? undefined,
        body: body ?? undefined,
    };
}
export function encodeMailtoLink(params: IEmailParams): string {
    const uri = new URL(`mailto:${params.to}`);
    if (params.ccs) uri.searchParams.append("cc", params.ccs);
    if (params.subject) uri.searchParams.append("subject", params.subject);
    if (params.body) uri.searchParams.append("body", params.body);

    return uri.toString();
}
export function placeResultToAddressDto(
    place: google.maps.places.PlaceResult,
): AddressDto {
    const address: AddressDto = {};
    let address1 = "";
    let address2 = "";
    let postCode = "";
    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
        const componentType = component.types[0];
        switch (componentType) {
            case "subpremise":
                if (/^\d+$/.test(component.long_name.trim())) {
                    //if digits only, add '/' to address 1 instead. eg subpremise: 10, street_number: 350 => 10/350
                    address1 = `${component.long_name.trim()}/${address1}`;
                } else {
                    address2 = `${component.long_name} ${address2}`;
                }
                break;

            case "premise":
                address2 += component.long_name;
                break;

            case "street_number":
                address1 = `${address1}${component.long_name} `;
                break;

            case "route":
                address1 += component.short_name;
                break;

            case "postal_code":
                postCode = `${component.long_name}${postCode}`;
                break;

            case "postal_code_suffix":
                postCode = `${postCode}-${component.long_name}`;
                break;

            case "locality":
                address.city = component.long_name;
                break;

            case "administrative_area_level_1":
                address.stateCode = component.short_name;
                break;

            case "country":
                address.countryCode = component.short_name;
                break;
        }
    }
    address.address1 = address1.trim();
    address.address2 = address2.trim();
    address.postCode = postCode;
    address.latitude = place.geometry?.location?.lat();
    address.longitude = place.geometry?.location?.lng();
    address.googlePlaceId = place.place_id;
    // After filling the form with address components from the Autocomplete
    // prediction, set cursor focus on the second address line to encourage
    // entry of subpremise information such as apartment, unit, or floor number.
    //address2Field.focus();
    return address;
}
export const addMinutes = (date: Date, minutes: number) => {
    return new Date(date.getTime() + minutes * 60000);
};

export const formatNumberWithCommas = (value: number, decimals: number = 0) => {
    const formatter = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    return formatter.format(value);
};

export const formatCurrency = (
    amount: number,
    currencyCode: string = "AUD",
) => {
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "narrowSymbol", // shows $1.23 rather than A$1.23
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

export const getCurrencySymbol = (currencyCode: string = "AUD"): string => {
    const parts = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).formatToParts(0);

    return parts
        .filter((part) => part.type === "currency")
        .map((part) => part.value)
        .join("");
};

export const clearExternalOrganisationId = () => {
    sessionStorage.removeItem(EXTERNAL_ORGANISATION_ID_HEADER);
};

export const getExternalOrganisationId = () => {
    const externalOrganisationId =
        sessionStorage.getItem(EXTERNAL_ORGANISATION_ID_HEADER) ?? undefined;

    return externalOrganisationId;
};
export const getPermissionInspectorEnabled = (): boolean => {
    return sessionStorage.getItem(ENABLE_PERMISSION_INSPECTOR) === "true";
};
export const setPermissionInspectorEnabled = (enabled: boolean) => {
    sessionStorage.setItem(ENABLE_PERMISSION_INSPECTOR, enabled.toString());
};

export const getMessageHeaders = () => {
    const headers: Record<string, string> = {};
    const externalOrganisationId = getExternalOrganisationId();

    if (externalOrganisationId) {
        headers[EXTERNAL_ORGANISATION_ID_HEADER] = externalOrganisationId;
    }

    return headers;
};

export const parseJsonSafe = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        console.warn(e);
        return null;
    }
};

/**
 * Formats a JSON string or object into a pretty-printed string
 * @param json - String or object to format
 * @returns Pretty-printed JSON string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatJsonPretty = (json?: any): string => {
    if (!json) return "";

    const parsedJSON = typeof json === "string" ? parseJsonSafe(json) : json;
    return parsedJSON
        ? JSON.stringify(parsedJSON, null, 2)
        : typeof json === "string"
          ? json
          : JSON.stringify(json);
};

export const extractFaultCode = (
    reason: string,
    deviceType: DeviceDtoDeviceType,
) => {
    const reasonJson = parseJsonSafe(reason);
    if (!reasonJson) return null;

    switch (deviceType) {
        case DeviceDtoDeviceType.ChargePoint:
            // OCPP 2.x shapes (StatusNotification, NotifyEvent, TransactionEvent) carry a `message`
            // field instead of vendor codes — no extractable fault code
            if (reasonJson.message !== undefined) return null;
            // OCPP 1.6J: prefer vendor-specific code, fall back to OCPP standard error code
            return reasonJson.vendorErrorCode || reasonJson.errorCode || null;
        case DeviceDtoDeviceType.PayMate:
            // NotifySystemAlert shape: severity is the code
            if (reasonJson.severity !== undefined)
                return reasonJson.severity || null;
            // NotifySystemErrors shape: "Error N" keys hold the error enum value
            return reasonJson["Error 0"] || null;
        case DeviceDtoDeviceType.Core: {
            // errors is a semicolon-delimited "E001: Overvoltage; E002: ..." string
            if (reasonJson.errors) {
                const firstErrorId = (reasonJson.errors as string)
                    .split(";")[0]
                    ?.split(":")[0]
                    ?.trim();
                return firstErrorId || null;
            }
            return null;
        }
        case DeviceDtoDeviceType.Wave: {
            // Arbitrary StringErrors dict from JCWP protocol — return first error value
            const firstEntry = Object.entries(reasonJson).find(
                ([key]) => key !== "deviceServiceInstance",
            );
            return firstEntry?.[1] || null;
        }
        default:
            return null;
    }
};

export const extractFaultEvent = (
    reason: string,
    deviceType: DeviceDtoDeviceType,
) => {
    const reasonJson = parseJsonSafe(reason);
    if (!reasonJson) return null;

    switch (deviceType) {
        case DeviceDtoDeviceType.ChargePoint:
            // OCPP 2.x shapes: return the message type as the event descriptor
            if (reasonJson.message !== undefined)
                return reasonJson.message || null;
            // OCPP 1.6J: info is the human-readable fault description
            return reasonJson.info || null;
        case DeviceDtoDeviceType.PayMate:
            // NotifySystemAlert shape: description is the event
            if (reasonJson.severity !== undefined)
                return reasonJson.description || null;
            // NotifySystemErrors shape: collect all "Error N" values
            return (
                Object.keys(reasonJson)
                    .filter((key) => key.startsWith("Error"))
                    .map((key) => reasonJson[key])
                    .join(", ") || null
            );
        case DeviceDtoDeviceType.Core:
            return reasonJson.errors || null;
        case DeviceDtoDeviceType.Wave: {
            // Arbitrary StringErrors dict — format all error entries as "key: value" pairs
            const errorEntries = Object.entries(reasonJson).filter(
                ([key]) => key !== "deviceServiceInstance",
            );
            return (
                errorEntries
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(", ") || null
            );
        }
        default:
            return null;
    }
};

export const toggleItemInArray = <T>(
    initialArray: T[],
    toggledItem: T,
    keyToCompare?: T extends object ? keyof T : undefined,
) => {
    const itemAlreadyExists = keyToCompare
        ? initialArray.some(
              (arrayItem) =>
                  arrayItem[keyToCompare] === toggledItem[keyToCompare],
          )
        : initialArray.includes(toggledItem);

    return itemAlreadyExists
        ? initialArray.filter((arrayItem: T) => {
              if (keyToCompare) {
                  return arrayItem[keyToCompare] !== toggledItem[keyToCompare];
              }
              return arrayItem !== toggledItem;
          })
        : [...initialArray, toggledItem];
};

export const diffArrays = <T>(
    original: T[],
    updated: T[],
): { removed: T[]; added: T[] } => {
    const originalSet = new Set(original);
    const updatedSet = new Set(updated);

    const removed = original.filter((item) => !updatedSet.has(item));
    const added = updated.filter((item) => !originalSet.has(item));

    return { removed, added };
};

export const shouldShowDeviceStatus = (
    flags: LDFlagSet,
    { deviceType, fwVersion }: Pick<DeviceDto, "deviceType" | "fwVersion">,
): boolean => {
    if (deviceType === DeviceDtoDeviceType.Core) {
        if (!flags[FeatureFlag.CORE_STATUS] || !fwVersion) return false;
        const minFwVersion =
            flags[FeatureFlag.CONFIG_IL_2488_MIN_CORE_FW_VERSION_FOR_STATUS];
        const fwVersionValidated = semver.valid(semver.coerce(fwVersion));

        return (
            minFwVersion &&
            fwVersionValidated &&
            semver.gte(fwVersionValidated, minFwVersion)
        );
    }
    if (deviceType == DeviceDtoDeviceType.PowerMeter)
        return !!flags[FeatureFlag.POWER_METER_STATUS_FLAG];
    return true;
};

/**
 * Performs a lazy match between two strings.
 * A lazy match is defined as checking if the first string contains the second string
 * after both strings have been converted to lowercase and stripped of whitespace.
 * @param a The first string to match.
 * @param b The second string to match.
 * @returns True if the first string lazily matches the second string, false otherwise.
 */
export const lazyMatch = (
    a: string | null | undefined,
    b: string | null | undefined,
) => {
    if (a === undefined || a === null || b === undefined || b === null) {
        return false;
    }
    return a
        .toLowerCase()
        .replace(/\s+/g, "")
        .includes(b.toLowerCase().replace(/\s+/g, ""));
};

// Helper function to check if a value is considered "empty".
// i.e. values of empty array, empty object, null, undefined, empty string, and false
export const isEmpty = (value: unknown): boolean => {
    if (
        value === null ||
        value === undefined ||
        value === false ||
        value === ""
    ) {
        return true;
    }

    if (Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === "object" && value !== null) {
        return Object.keys(value).length === 0;
    }

    return false;
};

export const unwrapODataValue = <Type>(value: unknown): Type | undefined => {
    if (value && typeof value === "object" && "_value" in value) {
        return (value as { _value?: Type })._value;
    }
    return value as Type;
};

// Recursively clean an object of "empty" values
// Useful for cleaning up search params
export const removeEmptyKeys = (
    obj: Record<string, unknown>,
): Record<string, string | string[]> => {
    const cleaned: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
        if (!isEmpty(value)) {
            if (
                typeof value === "object" &&
                value !== null &&
                !Array.isArray(value)
            ) {
                // Recursively clean nested objects
                const cleanedNestedObject = removeEmptyKeys(
                    value as Record<string, unknown>,
                );
                if (!isEmpty(cleanedNestedObject)) {
                    cleaned[key] = cleanedNestedObject;
                }
            } else {
                cleaned[key] = value;
            }
        }
    }

    return cleaned as Record<string, string | string[]>;
};

/**
 * Returns the site tag for a device
 * @param device
 * @returns string of the site tag (if exists)
 */
export const getSiteTag = (
    device?: Pick<DeviceDto, "deviceGroupTags">,
): string | undefined =>
    device?.deviceGroupTags?.find((tag) => tag.startsWith(SITE_TAG_PREFIX));

export const splitSiteTags = (
    tags: string[] | undefined,
): { deviceGroupTags: string[]; siteTags: string[] } =>
    (tags ?? []).reduce(
        (accumulator, tag) => {
            if (tag.startsWith(SITE_TAG_PREFIX)) {
                accumulator.siteTags.push(tag);
            } else {
                accumulator.deviceGroupTags.push(tag);
            }
            return accumulator;
        },
        { deviceGroupTags: [] as string[], siteTags: [] as string[] },
    );

export const getServiceModelTag = (
    device?: Pick<DeviceDto, "deviceGroupTags">,
): string | undefined =>
    device?.deviceGroupTags?.find((tag) =>
        tag.startsWith(SERVICE_MODEL_TAG_PREFIX),
    );

export const getDeviceLabel = (device: DeviceDto): string | undefined =>
    `${device.deviceType} ${device.deviceIdentity}${device.friendlyName ? " - " + device.friendlyName : ""}${device.deviceArchiveId ? " (archived)" : ""}`;
